/**
 * SendGridTemplateModal Component
 * Modal for searching and selecting SendGrid email templates
 */

import { useState, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import {
  listSendGridTemplates,
  getSendGridTemplateDetails,
  upsertTemplateRegistry,
  type SendGridTemplate,
  type SendGridTemplateDetails,
} from "../../services/templates.service";

interface SendGridTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: SendGridTemplateDetails) => void;
  selectedTemplateId?: string;
  /**
   * When true, each template row shows a star to SAVE it to the shared saved-templates list
   * (email_template_registry). Opt-in so existing campaign usage is unchanged. `onSavedToList` fires
   * after a successful save so the opener can refresh its saved chips.
   */
  enableSaveToSavedList?: boolean;
  onSavedToList?: () => void;
}

export default function SendGridTemplateModal({
  isOpen,
  onClose,
  onSelect,
  selectedTemplateId,
  enableSaveToSavedList = false,
  onSavedToList,
}: SendGridTemplateModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [templates, setTemplates] = useState<SendGridTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<
    SendGridTemplate[]
  >([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<SendGridTemplate | null>(null);
  const [templateDetails, setTemplateDetails] =
    useState<SendGridTemplateDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  // Save-to-saved-list (star) state.
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  // Load templates on mount
  useEffect(() => {
    if (isOpen && templates.length === 0) {
      loadTemplates();
    }
  }, [isOpen]);

  // Filter templates when search changes
  useEffect(() => {
    filterTemplates(searchTerm);
  }, [searchTerm, templates]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setSelectedTemplate(null);
      setTemplateDetails(null);
      setError(null);
      setPreviewMode(false);
      setSavingId(null);
      setSavedIds(new Set());
    }
  }, [isOpen]);

  // Save a template to the SHARED saved-templates list. Needs full details (merge_fields drive the
  // registry's capability flags), so fetch them before upserting. Row click still selects; this star
  // is a distinct, stop-propagated action.
  const handleSaveToSavedList = async (
    e: React.MouseEvent,
    template: SendGridTemplate,
  ) => {
    e.stopPropagation();
    if (savingId || savedIds.has(template.id)) {
      return;
    }
    setSavingId(template.id);
    setError(null);
    try {
      const details = await getSendGridTemplateDetails(template.id);
      // The star only renders in the contact Send-Email context (enableSaveToSavedList), so this
      // always saves to the CONTACT saved list. Campaign usage never enables the star.
      await upsertTemplateRegistry(details, { savedForContactEmail: true });
      setSavedIds((prev) => new Set(prev).add(template.id));
      onSavedToList?.();
    } catch (err) {
      console.error("Error saving template to saved list:", err);
      setError("Couldn't save that template to the saved list. Please retry.");
    } finally {
      setSavingId(null);
    }
  };

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSendGridTemplates();
      setTemplates(data);
      setFilteredTemplates(data);
    } catch (err) {
      console.error("Error loading templates:", err);
      setError("Failed to load templates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = useCallback(
    debounce((term: string) => {
      if (!term.trim()) {
        setFilteredTemplates(templates);
        return;
      }

      const lowerTerm = term.toLowerCase();
      setFilteredTemplates(
        templates.filter(
          (t) =>
            t.name.toLowerCase().includes(lowerTerm) ||
            t.active_version?.subject?.toLowerCase().includes(lowerTerm),
        ),
      );
    }, 200),
    [templates],
  );

  const handleSelectTemplate = async (template: SendGridTemplate) => {
    setSelectedTemplate(template);
    setLoadingDetails(true);
    setError(null);

    try {
      const details = await getSendGridTemplateDetails(template.id);
      setTemplateDetails(details);
    } catch (err) {
      console.error("Error loading template details:", err);
      setError("Failed to load template details.");
      setTemplateDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleConfirmSelection = () => {
    if (templateDetails) {
      onSelect(templateDetails);
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Select Email Template
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="px-4 py-3 bg-red-50 border-b border-red-100">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Content */}
          <div className="flex h-96">
            {/* Template List */}
            <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary-600 border-r-transparent"></div>
                  <p className="mt-2 text-sm text-gray-500">
                    Loading templates...
                  </p>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchTerm
                    ? "No templates found matching your search"
                    : "No templates available"}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedTemplate?.id === template.id
                          ? "bg-primary-50 border-l-4 border-primary-500"
                          : ""
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {template.name}
                          </p>
                          {template.active_version?.subject && (
                            <p className="mt-1 text-xs text-gray-500 truncate">
                              Subject: {template.active_version.subject}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Updated:{" "}
                            {new Date(template.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                        {selectedTemplateId === template.id && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Current
                          </span>
                        )}
                        {enableSaveToSavedList && (
                          <button
                            type="button"
                            onClick={(e) => handleSaveToSavedList(e, template)}
                            disabled={savingId === template.id}
                            title={
                              savedIds.has(template.id)
                                ? "Saved to your saved templates"
                                : "Save to saved templates"
                            }
                            aria-label={
                              savedIds.has(template.id)
                                ? "Saved to saved templates"
                                : "Save to saved templates"
                            }
                            className="ml-2 flex-shrink-0 p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                          >
                            <svg
                              className={`w-5 h-5 ${
                                savedIds.has(template.id)
                                  ? "text-amber-400"
                                  : "text-gray-300 hover:text-amber-400"
                              }`}
                              fill={
                                savedIds.has(template.id)
                                  ? "currentColor"
                                  : "none"
                              }
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Template Preview */}
            <div className="w-1/2 overflow-y-auto flex flex-col">
              {loadingDetails ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary-600 border-r-transparent"></div>
                    <p className="mt-2 text-sm text-gray-500">
                      Loading details...
                    </p>
                  </div>
                </div>
              ) : templateDetails ? (
                <div className="flex flex-col h-full">
                  {/* Toggle: Details / Preview */}
                  <div className="flex border-b border-gray-200 px-4 pt-3">
                    <button
                      type="button"
                      onClick={() => setPreviewMode(false)}
                      className={`pb-2 px-3 text-xs font-medium border-b-2 transition-colors ${
                        !previewMode
                          ? "border-primary-600 text-primary-600"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Details
                    </button>
                    {templateDetails.html_content && (
                      <button
                        type="button"
                        onClick={() => setPreviewMode(true)}
                        className={`pb-2 px-3 text-xs font-medium border-b-2 transition-colors ${
                          previewMode
                            ? "border-primary-600 text-primary-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        Preview
                      </button>
                    )}
                  </div>

                  {previewMode && templateDetails.html_content ? (
                    <div className="flex-1 p-2">
                      <iframe
                        srcDoc={templateDetails.html_content}
                        sandbox="allow-same-origin"
                        className="w-full h-full border border-gray-200 rounded"
                        title="Email template preview"
                      />
                    </div>
                  ) : (
                    <div className="p-4 space-y-4 overflow-y-auto">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {templateDetails.name}
                        </h3>
                        <p className="mt-1 text-xs text-gray-500">
                          Version: {templateDetails.version.name}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                          Subject Line
                        </h4>
                        <p className="mt-1 text-sm text-gray-900 bg-gray-50 rounded px-2 py-1">
                          {templateDetails.version.subject || "(No subject)"}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                          Text Preview
                        </h4>
                        <p className="mt-1 text-sm text-gray-600 bg-gray-50 rounded px-2 py-2 max-h-24 overflow-y-auto">
                          {templateDetails.preview || "(No preview available)"}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                          Merge Fields Detected (
                          {templateDetails.merge_fields.length})
                        </h4>
                        {templateDetails.merge_fields.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {templateDetails.merge_fields.map((field) => (
                              <span
                                key={field}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {`{{${field}}}`}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500 italic">
                            No merge fields detected in template
                          </p>
                        )}
                        <p className="mt-2 text-xs text-gray-400">
                          You can map these fields to contact data after
                          selecting
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedTemplate ? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-sm text-gray-500">
                    Click a template to see details
                  </p>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">
                      Select a template from the list
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              {filteredTemplates.length} template
              {filteredTemplates.length !== 1 ? "s" : ""} available
            </span>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSelection}
                disabled={!templateDetails}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Select Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
